/* ==========================================================================
   EKNO theme.js — shared utilities and global custom elements.
   No framework, no dependencies. Every element is progressively enhanced.
   ========================================================================== */
(function () {
  'use strict';
  const EKNO = window.EKNO || {};
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ utilities */
  const utils = {
    debounce(fn, wait = 200) {
      let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    },
    fetchConfig(type = 'json') {
      return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: `application/${type}` } };
    },
    formatMoney(cents, format) {
      if (EKNO.priceCustom !== null && EKNO.priceCustom !== undefined && !format) {
        const v = Number(cents) / 100;
        let str = v.toFixed(2).replace(/\.?0+$/, '');
        return EKNO.priceCustom ? str + ' ' + EKNO.priceCustom : str;
      }
      const fmt = format || (EKNO.showCurrencyCode ? EKNO.moneyWithCurrencyFormat : EKNO.moneyFormat) || '{{amount}}';
      const value = Number(cents) / 100;
      const rx = /\{\{\s*(\w+)\s*\}\}/;
      const m = fmt.match(rx);
      const key = m ? m[1] : 'amount';
      let out;
      const num = (v, dec, thousands, decimal) => {
        const parts = v.toFixed(dec).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };
      switch (key) {
        case 'amount_no_decimals': out = num(value, 0, ',', '.'); break;
        case 'amount_with_comma_separator': out = num(value, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': out = num(value, 0, '.', ','); break;
        case 'amount_with_space_separator': out = num(value, 2, ' ', ','); break;
        case 'amount_no_decimals_with_space_separator': out = num(value, 0, ' ', ','); break;
        case 'amount_with_apostrophe_separator': out = num(value, 2, "'", '.'); break;
        case 'amount_with_period_and_space_separator': out = num(value, 2, ' ', '.'); break;
        case 'amount_no_decimals_with_period_and_space_separator': out = num(value, 0, ' ', '.'); break;
        case 'amount_with_comma_separator_and_three_decimals': out = num(value, 3, '.', ','); break;
        case 'amount_with_three_decimals': out = num(value, 3, ',', '.'); break;
        default: out = num(value, 2, ',', '.');
      }
      return fmt.replace(rx, out);
    },
    toast(message, timeout = 3200) {
      const el = document.querySelector('ekno-toast');
      if (!el) return;
      el.textContent = message;
      el.classList.add('is-visible');
      clearTimeout(el._t);
      el._t = setTimeout(() => el.classList.remove('is-visible'), timeout);
    },
    focusables(root) {
      return Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, details'))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
    },
    trapFocus(container, initial) {
      utils.removeTrapFocus();
      const first = () => utils.focusables(container)[0];
      const last = () => { const f = utils.focusables(container); return f[f.length - 1]; };
      const handler = (e) => {
        if (e.key !== 'Tab') return;
        const f = first(); const l = last();
        if (!f) { e.preventDefault(); return; }
        if (e.shiftKey && (document.activeElement === f || document.activeElement === container)) { e.preventDefault(); l.focus(); }
        else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
      };
      document.addEventListener('keydown', handler);
      utils._trap = handler;
      (initial || first() || container).focus({ preventScroll: true });
    },
    removeTrapFocus() {
      if (utils._trap) document.removeEventListener('keydown', utils._trap);
      utils._trap = null;
    },
    lockScroll(lock) {
      const count = (document.body._lockCount || 0) + (lock ? 1 : -1);
      document.body._lockCount = Math.max(0, count);
      document.body.classList.toggle('no-scroll', document.body._lockCount > 0);
    },
    parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); },
    sectionHTML(html, selector) { const doc = utils.parseHTML(html); return selector ? doc.querySelector(selector) : doc; },
    supportsWebGL() {
      try { const c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; }
    },
    isMobile() { return window.matchMedia('(max-width: 749px)').matches; },
    isTouch() { return window.matchMedia('(hover: none)').matches; },
    reducedMotion() { return prefersReducedMotion.matches; },
    pubsub: (() => {
      const subs = {};
      return {
        on(evt, fn) { (subs[evt] = subs[evt] || []).push(fn); return () => { subs[evt] = subs[evt].filter((f) => f !== fn); }; },
        emit(evt, data) { (subs[evt] || []).forEach((fn) => fn(data)); document.dispatchEvent(new CustomEvent(evt, { detail: data })); }
      };
    })()
  };
  window.EKNO = Object.assign(EKNO, { utils });

  /* ------------------------------------------------------- Header heights */
  const setHeaderVars = () => {
    const header = document.querySelector('.header__bar');
    if (header) document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };
  setHeaderVars();
  if ('ResizeObserver' in window) {
    const hb = document.querySelector('.header__bar');
    if (hb) new ResizeObserver(setHeaderVars).observe(hb);
  }

  /* ------------------------------------------------------------- Drawer */
  class EknoDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); this.close(); } });
      this.syncInlineState = () => {
        const inline = this.matches('.facets-drawer:not(.facets-drawer--always)') && window.matchMedia('(min-width: 990px)').matches;
        if (inline) {
          this.removeAttribute('aria-hidden');
          this.panel?.removeAttribute('role'); this.panel?.removeAttribute('aria-modal');
        } else if (!this.classList.contains('is-open')) {
          this.setAttribute('aria-hidden', 'true');
          if (this.panel && !this.panel.hasAttribute('role')) { this.panel.setAttribute('role', 'dialog'); this.panel.setAttribute('aria-modal', 'true'); }
        }
      };
      this.syncInlineState();
      const mq = window.matchMedia('(min-width: 990px)');
      if (mq.addEventListener) mq.addEventListener('change', this.syncInlineState);
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      utils.lockScroll(true);
      document.querySelector('.header')?.classList.add('is-open');
      const focusTarget = this.querySelector('[data-autofocus]') || this.panel;
      setTimeout(() => utils.trapFocus(this.panel, focusTarget), 40);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      utils.removeTrapFocus();
      utils.lockScroll(false);
      if (!document.querySelector('ekno-drawer.is-open')) document.querySelector('.header')?.classList.remove('is-open');
      if (this.opener && this.opener.setAttribute) { this.opener.setAttribute('aria-expanded', 'false'); this.opener.focus?.({ preventScroll: true }); }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
    toggle(opener) { this.classList.contains('is-open') ? this.close() : this.open(opener); }
  }
  customElements.define('ekno-drawer', EknoDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) { e.preventDefault(); drawer.open(opener); }
    }
    const searchOpener = e.target.closest('[data-search-open]');
    if (searchOpener) {
      const drawer = document.getElementById('SearchDrawer');
      if (drawer) { e.preventDefault(); drawer.open(searchOpener); setTimeout(() => drawer.querySelector('input[type="search"]')?.focus(), 60); }
    }
    const cartOpener = e.target.closest('[data-cart-open]');
    if (cartOpener && EKNO.cartType === 'drawer') {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && drawer.open) { e.preventDefault(); drawer.open(cartOpener); }
    }
  });

  /* ------------------------------------------------------------- Header */
  class EknoHeader extends HTMLElement {
    connectedCallback() {
      this.threshold = 24;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.initPinnedObserver();
      this.initDropdowns();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); this.pinIO?.disconnect(); }
    setScrolled(scrolled) {
      if (scrolled !== this._scrolled) { this._scrolled = scrolled; this.classList.toggle('is-scrolled', scrolled); }
    }
    onScroll() {
      // Window scroll (desktop / normal pages). The pinned observer below covers scroll containers,
      // the Theme Editor mobile preview and in-app browsers where window.scrollY never changes.
      if (window.scrollY > this.threshold) this.setScrolled(true);
      else if (!this._pinned) this.setScrolled(false);
    }
    initPinnedObserver() {
      if (!('IntersectionObserver' in window)) return;
      // A sticky element is "pinned" once its top edge reaches the viewport top: with a -1px top
      // rootMargin it is no longer fully intersecting, which is a reliable signal in any scroll context.
      const target = this.closest('.section-header') || this;
      this.pinIO = new IntersectionObserver(([entry]) => {
        const pinned = entry.intersectionRatio < 1 && entry.boundingClientRect.top <= 1;
        this._pinned = pinned;
        this.setScrolled(pinned || window.scrollY > this.threshold);
      }, { threshold: [1], rootMargin: '-1px 0px 0px 0px' });
      this.pinIO.observe(target);
    }
    initDropdowns() {
      const items = this.querySelectorAll('[data-dropdown]');
      const closeAll = (except) => items.forEach((it) => { if (it !== except) { it.classList.remove('is-open'); it.querySelector('.header__link')?.setAttribute('aria-expanded', 'false'); } });
      items.forEach((item) => {
        const link = item.querySelector('.header__link');
        let timer;
        const open = () => { clearTimeout(timer); closeAll(item); item.classList.add('is-open'); link.setAttribute('aria-expanded', 'true'); };
        const close = () => { timer = setTimeout(() => { item.classList.remove('is-open'); link.setAttribute('aria-expanded', 'false'); }, 120); };
        if (!utils.isTouch()) { item.addEventListener('mouseenter', open); item.addEventListener('mouseleave', close); }
        link.addEventListener('click', (e) => {
          if (utils.isTouch() && !item.classList.contains('is-open')) { e.preventDefault(); open(); }
        });
        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || (e.key === 'Enter' && e.altKey)) { e.preventDefault(); open(); item.querySelector('.header__sublink')?.focus(); }
          if (e.key === 'Escape') { close(); link.focus(); }
        });
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { item.classList.remove('is-open'); link.setAttribute('aria-expanded', 'false'); link.focus(); } });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) closeAll(); });
    }
  }
  customElements.define('ekno-header', EknoHeader);

  /* -------------------------------------------------------- Announcement */
  class EknoAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.io = new IntersectionObserver((entries) => entries[0].isIntersecting ? this.start() : this.stop());
      this.io.observe(this);
    }
    go(dir, manual) {
      this.slides[this.index].classList.remove('is-active'); this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = (this.index + dir + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active'); this.slides[this.index].removeAttribute('aria-hidden');
      if (manual) { this.stop(); this.start(); }
    }
    start() { if (utils.reducedMotion()) return; this.stop(); this.timer = setInterval(() => this.go(1), this.interval); }
    stop() { clearInterval(this.timer); }
  }
  customElements.define('ekno-announcement', EknoAnnouncement);

  /* ------------------------------------------------------------ Carousel */
  class EknoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.dots = this.querySelector('.carousel__dots');
      this.prev?.addEventListener('click', () => this.scrollBy(-1));
      this.next?.addEventListener('click', () => this.scrollBy(1));
      this.track.addEventListener('scroll', utils.debounce(() => this.update(), 80), { passive: true });
      window.addEventListener('resize', utils.debounce(() => this.update(), 200));
      this.buildDots();
      this.update();
    }
    slideWidth() { const s = this.track.firstElementChild; return s ? s.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0) : 0; }
    scrollBy(dir) {
      const w = this.slideWidth() || this.track.clientWidth;
      const rtl = document.documentElement.dir === 'rtl';
      this.track.scrollBy({ left: dir * w * (rtl ? -1 : 1), behavior: utils.reducedMotion() ? 'auto' : 'smooth' });
    }
    buildDots() {
      if (!this.dots) return;
      const n = this.track.children.length;
      const visible = Math.max(1, Math.round(this.track.clientWidth / (this.slideWidth() || this.track.clientWidth)));
      const pages = Math.max(1, Math.ceil(n / visible));
      this.dots.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'carousel__dot'; b.setAttribute('aria-label', `${i + 1}/${pages}`);
        b.addEventListener('click', () => this.track.scrollTo({ left: i * this.track.clientWidth * (document.documentElement.dir === 'rtl' ? -1 : 1), behavior: 'smooth' }));
        this.dots.appendChild(b);
      }
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const x = Math.abs(this.track.scrollLeft);
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max;
      if (this.dots) {
        const page = Math.round(x / Math.max(1, this.track.clientWidth));
        Array.from(this.dots.children).forEach((d, i) => d.setAttribute('aria-current', i === page ? 'true' : 'false'));
      }
      this.classList.toggle('carousel--overflow', this.track.scrollWidth > this.track.clientWidth + 2);
    }
  }
  customElements.define('ekno-carousel', EknoCarousel);

  /* ----------------------------------------------------------- Quantity */
  class EknoQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
      this.input?.addEventListener('change', () => this.validate());
      this.validate();
    }
    step(dir) { const v = (parseInt(this.input.value, 10) || 0) + dir; this.input.value = v; this.validate(); this.input.dispatchEvent(new Event('change', { bubbles: true })); }
    validate() {
      if (!this.input) return;
      const min = parseInt(this.input.min, 10) || 1; const max = parseInt(this.input.max, 10) || Infinity;
      let v = parseInt(this.input.value, 10) || min; v = Math.min(max, Math.max(min, v)); this.input.value = v;
      const minus = this.querySelector('[data-qty-minus]'); const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.disabled = v <= min; if (plus) plus.disabled = v >= max;
    }
  }
  customElements.define('ekno-quantity', EknoQuantity);

  /* ------------------------------------------------------- Toast element */
  class EknoToast extends HTMLElement {}
  customElements.define('ekno-toast', EknoToast);

  /* ------------------------------------------------------- Reveal on scroll */
  const initReveal = () => {
    if (!EKNO.animations || utils.reducedMotion() || !('IntersectionObserver' in window)) { document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => io.observe(el));
    EKNO.observeReveal = (root) => root.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => io.observe(el));
  };
  initReveal();
  document.addEventListener('shopify:section:load', () => { initReveal(); });

  /* -------------------------------------------------------------- Wishlist */
  const wishlist = {
    key: 'ekno_wishlist',
    read() { try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { return []; } },
    write(list) { try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) { /* private mode */ } this.sync(); },
    has(handle) { return this.read().includes(handle); },
    toggle(handle) { const list = this.read(); const i = list.indexOf(handle); if (i > -1) list.splice(i, 1); else list.push(handle); this.write(list); return i === -1; },
    sync() {
      const list = this.read();
      document.querySelectorAll('[data-wishlist]').forEach((btn) => {
        const on = list.includes(btn.dataset.handle);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-label', on ? (EKNO.strings.wishlistRemoved || 'Remove from wishlist') : (EKNO.strings.wishlistAdded || 'Add to wishlist'));
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((c) => { c.textContent = list.length; c.hidden = list.length === 0; });
    }
  };
  EKNO.wishlist = wishlist;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;
    e.preventDefault();
    const detail = { handle: btn.dataset.handle, productId: btn.dataset.productId, title: btn.dataset.title, button: btn };
    if (EKNO.wishlistMode === 'app') { utils.pubsub.emit('ekno:wishlist:toggle', detail); return; }
    if (EKNO.wishlistMode === 'off') return;
    const added = wishlist.toggle(detail.handle);
    utils.pubsub.emit('ekno:wishlist:toggle', Object.assign(detail, { added }));
    utils.toast(added ? EKNO.strings.wishlistAdded : EKNO.strings.wishlistRemoved);
  });
  if (EKNO.wishlistMode === 'local') wishlist.sync();
  document.addEventListener('ekno:dom:updated', () => { if (EKNO.wishlistMode === 'local') wishlist.sync(); });

  /* ---------------------------------------------------- Card swatch swap */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-swatch-link]');
    if (!link || !link.dataset.image) return;
    const card = link.closest('[data-product-card]');
    const img = card?.querySelector('.product-card__img--primary');
    if (!img) return;
    e.preventDefault();
    img.src = link.dataset.image; img.removeAttribute('srcset');
    card.querySelectorAll('.swatch').forEach((s) => s.classList.remove('is-active'));
    link.querySelector('.swatch')?.classList.add('is-active');
    const titleLink = card.querySelector('.product-card__title a'); if (titleLink) titleLink.href = link.href;
  });

  /* -------------------------------------------------------- Localization */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-auto-submit]');
    if (sel) sel.form?.submit();
  });

  /* ------------------------------------------------------- Quick view */
  const quickView = {
    dialog: null,
    ensure() {
      if (this.dialog) return this.dialog;
      const d = document.createElement('dialog');
      d.className = 'modal quick-view-modal';
      d.innerHTML = `<div class="modal__panel"><button type="button" class="icon-btn modal__close" data-modal-close aria-label="${EKNO.strings.close}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5"><path d="M6 6l12 12M18 6L6 18"/></svg></button><div class="quick-view__content" aria-live="polite"></div></div>`;
      d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-modal-close]')) this.close(); });
      d.addEventListener('close', () => utils.lockScroll(false));
      document.getElementById('QuickViewRoot')?.appendChild(d);
      this.dialog = d; return d;
    },
    async open(url, opener) {
      const d = this.ensure();
      const content = d.querySelector('.quick-view__content');
      content.innerHTML = `<div class="quick-view__loading"><div class="skeleton media media--square"></div><div class="stack"><div class="skeleton skeleton--text" style="width:60%"></div><div class="skeleton skeleton--text" style="width:30%"></div><div class="skeleton" style="height:48px"></div></div></div>`;
      this.opener = opener;
      if (!d.open) { d.showModal(); utils.lockScroll(true); }
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${sep}section_id=quick-view`);
        if (!res.ok) throw new Error(res.statusText);
        const html = await res.text();
        const section = utils.sectionHTML(html, '.quick-view');
        content.innerHTML = '';
        if (section) content.appendChild(section);
        document.dispatchEvent(new CustomEvent('ekno:dom:updated', { detail: { root: content } }));
        content.querySelector('h2, [data-autofocus]')?.focus?.();
      } catch (err) {
        content.innerHTML = `<div class="empty-state"><p>${EKNO.strings.cartError}</p><a class="btn btn--secondary" href="${url}">${EKNO.strings.retry}</a></div>`;
      }
    },
    close() { if (this.dialog?.open) { this.dialog.close(); } this.opener?.focus?.(); }
  };
  EKNO.quickView = quickView;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (btn) { e.preventDefault(); quickView.open(btn.dataset.quickView, btn); }
  });

  /* ------------------------------------------------------- Quick add */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('btn--loading'); btn.setAttribute('aria-busy', 'true');
    try {
      const body = { items: [{ id: Number(btn.dataset.quickAdd), quantity: 1 }], sections: 'cart-drawer' };
      const res = await fetch(EKNO.routes.cartAdd + '.js', Object.assign(utils.fetchConfig(), { body: JSON.stringify(body) }));
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || EKNO.strings.cartError);
      utils.pubsub.emit('ekno:cart:added', { variantId: btn.dataset.quickAdd, title: btn.dataset.productTitle, price: Number(btn.dataset.price || 0), quantity: 1, sections: data.sections, item: data });
      utils.toast(EKNO.strings.addedToCart);
    } catch (err) { utils.toast(err.message || EKNO.strings.cartError); }
    finally { btn.classList.remove('btn--loading'); btn.removeAttribute('aria-busy'); }
  });

  /* ----------------------------------------------- Recently viewed tracker */
  EKNO.recentlyViewed = {
    key: 'ekno_recent',
    read() { try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { return []; } },
    add(handle) { if (!handle) return; const list = this.read().filter((h) => h !== handle); list.unshift(handle); try { localStorage.setItem(this.key, JSON.stringify(list.slice(0, 12))); } catch (e) {} }
  };

  /* ------------------------------------------------------ Details accordion */
  document.addEventListener('click', (e) => {
    const sum = e.target.closest('summary');
    if (!sum) return;
    const details = sum.parentElement; const group = details.closest('[data-accordion-single]');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach((d) => { if (d !== details) d.removeAttribute('open'); });
  });

  /* --------------------------------------------- Dialog polyfill guard */
  if (!('HTMLDialogElement' in window)) {
    HTMLElement.prototype.showModal = function () { this.setAttribute('open', ''); };
    HTMLElement.prototype.close = function () { this.removeAttribute('open'); };
  }
})();
